import { Info, Search, Sparkles } from "lucide-react";
import type { Mention } from "@/shared/types/mention";
import { MentionAiTraceSection } from "./MentionAiTrace";
import { MentionClassificationHistory } from "./MentionClassificationEditor";
import { MentionCenaSummary } from "./mention-cena-detail";
import { classificationMethodLabel, clippingLabel, describeOrigin } from "./mention-origin";
import { APPROACH_STARS, MentionClassification } from "./mention-classification";

/**
 * O miolo do "Mais informações": por que casou, o rastro da IA, origem,
 * id, histórico e o CENA. Era o corpo do modal de mesmo nome, que saiu
 * junto com a visão em cards (2026-10-13); hoje mora no card EXPANDIDO
 * da lista, que já mostra a classificação — daí `showClassification`.
 */
export function MentionDetailsBody({
  mention,
  token,
  showClassification = true
}: {
  mention: Mention;
  token: string;
  showClassification?: boolean;
}) {
  // Os termos chegam como a expressão gravada ('"Corsan" OR "esgoto"'
  // ou separados por ";"); aqui só viram chips, sem reinterpretar nada.
  const terms = (mention.foundTerms ?? "")
    .split(/;|\s+OR\s+/)
    .map(term => term.trim().replace(/^"(.*)"$/, "$1"))
    .filter(Boolean);
  const origin = describeOrigin(mention);
  return (
    <div className="mdb">
      {showClassification && (
        <section className="mdb-card">
          <h4 className="mdb-card-title"><Sparkles size={14}/> Classificação</h4>
          <div className="card-classification-bar">
            <MentionClassification mention={mention} stars={APPROACH_STARS[mention.approach] ?? 0}/>
          </div>
        </section>
      )}
      <div className="mdb-grid">
        <div className="mdb-main">
          {/* Dois "porquês" complementares, nesta ordem: primeiro o
              determinístico (por que este post casou com a marca),
              depois o da IA (por que ela classificou assim). */}
          <section className="mdb-card">
            <h4 className="mdb-card-title"><Search size={14}/> Por que esta menção</h4>
            <div className="mdb-field">
              <span className="mdb-label">Termos que casaram</span>
              {terms.length ? (
                <div className="mention-term-chips">
                  {terms.map(term => <span key={term} className="mention-term-chip">{term}</span>)}
                </div>
              ) : (
                <p className="mdb-text">Não registrado.</p>
              )}
            </div>
            <div className="mdb-field mdb-ai">
              <MentionAiTraceSection mention={mention} token={token}/>
            </div>
          </section>
          {/* A trilha de quem mexeu na classificação (2026-10-07: a
              dona abria o detalhe e achava que o histórico "não tinha
              carregado"). Some sozinha quando não há linha. */}
          <div className="mdb-history">
            <MentionClassificationHistory
              mentionId={mention.id}
              token={token}
              reloadKey={0}
            />
          </div>
        </div>
        <div className="mdb-side">
          <MentionCenaSummary mention={mention}/>
          <section className="mdb-card">
            <h4 className="mdb-card-title"><Info size={14}/> Informações</h4>
            <dl className="mdb-facts">
              <div>
                <dt>Origem</dt>
                <dd>Via {clippingLabel(mention.clippingService)}{mention.channel ? ` · ${mention.channel}` : ""}</dd>
              </div>
              <div>
                <dt>Classificação</dt>
                <dd>
                  {classificationMethodLabel(mention.classificationMethod)}
                  {origin && <small>{origin}</small>}
                </dd>
              </div>
              <div>
                <dt>ID</dt>
                <dd><code translate="no">{mention.id}</code></dd>
              </div>
            </dl>
          </section>
        </div>
      </div>
    </div>
  );
}
